import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, LabelList,
} from 'recharts';
import { getFastingVomit } from './api';

const PERIODS = [
  { label: '2 мес', value: 2 },
  { label: '6 мес', value: 6 },
  { label: '12 мес', value: 12 },
];

/** '14.07.26' → '14.07' — на оси год только мешает */
function shortDate(s) {
  return s ? s.slice(0, 5) : '';
}

const MONTHS_NOM = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];
const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** 'DD.MM.YY' → {m: 0-11, y} */
function monthOf(s) {
  const [, m, y] = s.split('.');
  return { m: +m - 1, y: +y };
}

/** Фраза про период без эпизодов: «За август и сентябрь…», «С апреля по сентябрь…» */
function noEpisodesText(from, to) {
  const a = monthOf(from);
  const b = monthOf(to);
  const count = (b.y - a.y) * 12 + (b.m - a.m) + 1;
  if (count <= 1) return `За ${MONTHS_NOM[b.m]} рвоты натощак не было`;
  if (count === 2) {
    return `За ${MONTHS_NOM[a.m]} и ${MONTHS_NOM[b.m]} рвоты натощак не было`;
  }
  return `С ${MONTHS_GEN[a.m]} по ${MONTHS_NOM[b.m]} рвоты натощак не было`;
}

function plural(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
  return 'дней';
}

export default function FastingVomitBlock({ petId }) {
  const [months, setMonths] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getFastingVomit(petId, months)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [petId, months]);

  const periodTabs = (
    <div className="visits-period-tabs ss-tabs">
      {PERIODS.map(p => (
        <button
          key={p.value}
          className={`visits-tab${months === p.value ? ' visits-tab-active' : ''}`}
          onClick={() => setMonths(p.value)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  const items = data?.items || [];

  // Столбцы и эпизоды идут одной последовательностью: у эпизода нет высоты,
  // его место на оси занимает пунктирная линия с датой.
  const chartData = items.map((it, i) => ({
    key: `${it.type}-${i}`,
    days: it.type === 'streak' ? it.days : null,
    item: it,
  }));
  const episodeRows = chartData.filter(r => r.item.type === 'episode');

  return (
    <div className="ss-block">
      <div className="ss-header">
        <div className="ss-title">Дни без рвоты натощак</div>
        {periodTabs}
      </div>

      {loading && <p className="fr-empty">Загрузка…</p>}

      {!loading && !data && (
        <p className="fr-empty">Не удалось загрузить данные</p>
      )}

      {!loading && data && data.current_days == null && (
        <p className="fr-empty">Нет записей о самочувствии</p>
      )}

      {!loading && data && data.current_days != null && (
        <>
          {/* Текущая серия — главное число блока */}
          <div className="fv-current">
            {data.current_days > 0 ? (
              <>
                <span className="fv-current-num">{data.current_days}</span>
                <span className="fv-current-unit">
                  {plural(data.current_days)} без рвоты натощак
                </span>
                {data.current_since && (
                  <span className="fv-current-since">с {data.current_since}</span>
                )}
              </>
            ) : (
              <span className="fv-current-reset">Рвота натощак была сегодня</span>
            )}
          </div>

          {items.length === 0 && (
            <p className="fr-empty">Нет данных за выбранный период</p>
          )}

          {/* Без эпизодов график выродился бы в один столбик без пунктиров —
              он ничего не сообщает, поэтому пишем период словами. */}
          {items.length > 0 && data.episodes_in_period === 0 && (
            <p className="fv-clean">
              {noEpisodesText(data.full_months_from, data.full_months_to)}
            </p>
          )}

          {items.length > 0 && data.episodes_in_period > 0 && (
            <>
              <ResponsiveContainer width="100%" height={190}>
                <BarChart data={chartData} margin={{ top: 26, right: 10, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe3" vertical={false} />
                  <XAxis dataKey="key" tick={false} height={4} stroke="#e8e0d5" />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#888' }}
                    allowDecimals={false}
                    width={34}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, fontSize: 13 }}
                    formatter={(v) => v != null ? [`${v} ${plural(v)}`, 'Без рвоты'] : [null, null]}
                    labelFormatter={(_, payload) => {
                      const it = payload?.[0]?.payload?.item;
                      if (!it || it.type !== 'streak') return '';
                      return `${it.date_from} — ${it.date_to}`;
                    }}
                  />
                  <Bar
                    dataKey="days"
                    fill="#6fa85f"
                    radius={[3, 3, 0, 0]}
                    minPointSize={3}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="days"
                      position="top"
                      style={{ fontSize: 10, fill: '#4f7a3f', fontWeight: 700 }}
                    />
                  </Bar>
                  {/* Эпизод рвоты — пунктир с датой, серию обрывает он */}
                  {episodeRows.map(r => (
                    <ReferenceLine
                      key={r.key}
                      x={r.key}
                      stroke="#c2553f"
                      strokeWidth={1.5}
                      strokeDasharray="3 3"
                      label={{
                        value: shortDate(r.item.date),
                        position: 'top',
                        fontSize: 9,
                        fill: '#c2553f',
                      }}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>

              <p className="ss-note fv-note">
                Столбец — серия дней без отметки о рвоте натощак, пунктир с датой — эпизод,
                который серию обрывает. Последний столбец растёт каждый день.
                {items.some(it => it.starts_before_window) &&
                  ' Первая серия началась раньше выбранного периода и показана целиком.'}
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
